import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import MediaBudget from "../components/MediaBudget";
import InvestmentPlan from "../components/InvestmentPlan";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import { formatDate, formatMoney } from "../api";
import { MetricCard, PageHeader } from "../components/Ui";
import {
  DataTable,
  NoClient,
  RequestView,
  useMutation,
  type RecordData,
  label,
} from "../components/Workflow";
export const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
export function monday() {
  const d = new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

type Snapshots = {
  snapshots: RecordData[];
  /** Every entry for the filters, not only the listed ones; from/to is their date span. */
  totals: { spend: number; clicks: number; conversions: number; rows: number; from: string | null; to: string | null };
  /** Platforms the nightly Google Ads sync records for this client: no entries here. */
  synced_platforms?: string[];
};
const LISTED = 500; // the API lists the latest 500 entries
const UNDO_SHOWN_MS = 10_000;

/** "Google Ads, LSA and YouTube". */
function platformNames(platforms: string[]) {
  const names = platforms.map(label);
  return names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0] || "";
}

/** "Jun 29 to Oct 1, 2026"; a span across two years names both. */
function dateSpan(from: string | null, to: string | null) {
  if (!from || !to) return undefined;
  if (from === to) return formatDate(to);
  const start = formatDate(from);
  return `${from.slice(0, 4) === to.slice(0, 4) ? start.replace(/, \d{4}$/, "") : start} to ${formatDate(to)}`;
}

function SpendForm({ clientId }: { clientId: number }) {
  const [date, setDate] = useState(monday),
    [draft, setDraft] = useState<
      Record<string, { spend: string; clicks: string; conversions: string }>
    >({}),
    [dirty, setDirty] = useState<string[]>([]),
    // The entry just deleted, kept so Undo can put it back.
    [removed, setRemoved] = useState<RecordData | null>(null);
  const platforms = useApi<{ platforms: string[] }>(
    "/api/performance/platforms",
  );
  const entries = useApi<Snapshots>(
    `/api/performance/${clientId}/snapshots?from=${date}&to=${date}`,
  );
  const history = useApi<Snapshots>(
    `/api/performance/${clientId}/snapshots`,
  );
  const mutation = useMutation();
  const historyAction = useMutation();
  useEffect(() => {
    if (!removed) return;
    const timer = window.setTimeout(() => setRemoved(null), UNDO_SHOWN_MS);
    return () => window.clearTimeout(timer);
  }, [removed]);
  const synced = entries.data?.synced_platforms ?? history.data?.synced_platforms ?? [];
  const editable = (platforms.data?.platforms || []).filter((p) => !synced.includes(p));
  useEffect(() => {
    if (!entries.data) return;
    const initial: typeof draft = {};
    // Only hand-typed entries fill the boxes: a synced figure saved from here would count twice.
    for (const row of entries.data.snapshots.filter((r) => !r.location_id && r.manual)) {
      initial[row.platform] = {
        spend: String(row.spend ?? ""),
        clicks: String(row.clicks ?? ""),
        conversions: String(row.conversions ?? ""),
      };
    }
    setDraft(initial);
    setDirty([]);
  }, [entries.data]);
  async function save(e: React.FormEvent) {
    e.preventDefault();
    const snapshots = dirty
      .filter((p) => draft[p]?.spend !== "" && !synced.includes(p))
      .map((platform) => ({
        platform,
        snapshot_date: date,
        spend: Number(draft[platform].spend),
        clicks:
          draft[platform].clicks === "" ? null : Number(draft[platform].clicks),
        conversions:
          draft[platform].conversions === ""
            ? null
            : Number(draft[platform].conversions),
      }));
    if (!snapshots.length) return;
    setRemoved(null);
    // The batch saves whole or not at all: a refused row comes back as the error above the button.
    const result = await mutation.run<{ created: number; updated: number }>(
      `/api/performance/${clientId}/snapshots/bulk`,
      { snapshots },
    );
    if (result) {
      const saved = result.created + result.updated;
      mutation.setNotice(`Saved ${saved} ${saved === 1 ? "entry" : "entries"} for ${formatDate(date)}.`);
      entries.reload();
      history.reload();
    }
  }
  async function remove(row: RecordData) {
    const done = await historyAction.run<{ deleted: RecordData }>(`/api/performance/snapshots/${row.id}`, {}, "DELETE");
    if (!done) return;
    setRemoved(done.deleted);
    // The form refills from the server only when the entry was on its date (a refill drops anything typed).
    if (done.deleted.snapshot_date === date) entries.reload();
    history.reload();
  }
  async function undo() {
    if (!removed) return;
    const { platform, snapshot_date, spend, impressions, clicks, conversions, location_id } = removed;
    const done = await historyAction.run(`/api/performance/${clientId}/snapshots`, { platform, snapshot_date, spend, impressions, clicks, conversions, location_id });
    if (!done) return;
    setRemoved(null);
    if (snapshot_date === date) entries.reload();
    history.reload();
  }
  const totals = history.data?.totals;
  const span = dateSpan(totals?.from ?? null, totals?.to ?? null);
  return (
    <div className="workflow-stack">
      <section className="panel workflow-panel spend-entry">
        <h2>Enter platform spend</h2>
        <p className="workflow-note">
          Enter a week’s total on its Monday, or one day’s spend. Leave a box
          blank to skip it; 0 means no spend.
          {/* Synced platforms are said here, once, not as a row of the entry table. */}
          {synced.length > 0 && <> {platformNames(synced)} sync nightly, so they are not listed.</>}
        </p>
        <label className="select-control standalone-filter">
          Entry date
          <input
            aria-label="Spend entry date"
            type="date"
            value={date}
            max={today()}
            onChange={(e) => {
              setDate(e.target.value);
              setDraft({});
              setDirty([]);
            }}
          />
        </label>
        <RequestView request={platforms}>
          <RequestView request={entries}>
            <form onSubmit={save}>
              <div className="table-wrap">
                <table className="workflow-table spend-entry-table">
                  <thead>
                    <tr>
                      <th>Platform</th>
                      <th>Spend ($)</th>
                      <th>Clicks</th>
                      <th>Conversions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editable.map((p) => (
                      <tr key={p}>
                        <td>{label(p)}</td>
                        {(["spend", "clicks", "conversions"] as const).map((field) => (
                          <td key={field} data-label={{ spend: "Spend ($)", clicks: "Clicks", conversions: "Conversions" }[field]}>
                            <input
                              aria-label={`${label(p)} ${field}`}
                              type="number"
                              inputMode={field === "spend" ? "decimal" : "numeric"}
                              min="0"
                              step={field === "spend" ? ".01" : "1"}
                              value={draft[p]?.[field] ?? ""}
                              onChange={(e) => {
                                setDraft((v) => ({
                                  ...v,
                                  [p]: {
                                    spend: v[p]?.spend ?? "",
                                    clicks: v[p]?.clicks ?? "",
                                    conversions: v[p]?.conversions ?? "",
                                    [field]: e.target.value,
                                  },
                                }));
                                setDirty((v) => [...new Set([...v, p])]);
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {mutation.feedback}
              <div className="workflow-actions">
                <button
                  className="button"
                  disabled={
                    mutation.busy || !dirty.some((p) => draft[p]?.spend !== "")
                  }
                >
                  {mutation.busy ? "Saving…" : "Save entries"}
                </button>
              </div>
            </form>
          </RequestView>
        </RequestView>
      </section>
      <section className="panel spend-history">
        <div className="panel-heading">
          <h2>Spend history</h2>
          <button className="button secondary" onClick={history.reload}>
            Refresh
          </button>
        </div>
        <RequestView request={history}>
          {historyAction.feedback}
          <div className="module-metrics">
            <MetricCard label="Total spend" value={formatMoney(Number(totals?.spend || 0))} detail={span} />
            <MetricCard label="Clicks" value={Number(totals?.clicks || 0).toLocaleString()} detail={span} />
            <MetricCard label="Conversions" value={Number(totals?.conversions || 0).toLocaleString()} detail={span} />
          </div>
          <DataTable
            pageSize={30}
            rows={history.data?.snapshots || []}
            // Location shows only when an entry has one; otherwise it is a column of dashes.
            columns={[
              "snapshot_date",
              "platform",
              ...((history.data?.snapshots || []).some((r) => r.location_name) ? ["location_name"] : []),
              "spend",
              "clicks",
              "conversions",
              "cpa",
            ]}
            // Hand-typed entries can be deleted; synced spend changes only in Google Ads.
            action={(history.data?.snapshots || []).some((r) => r.manual) ? (row) =>
              row.manual ? (
                <button type="button" className="text-button" disabled={historyAction.busy} onClick={() => void remove(row)}>
                  Delete
                </button>
              ) : null : undefined}
            empty="No spend recorded yet. Google Ads and LSA arrive nightly once an account is linked; enter other platforms above."
          />
          {totals && totals.rows > LISTED && (
            <p className="spend-listed">
              Listing the latest {LISTED.toLocaleString()} of {totals.rows.toLocaleString()} entries. The totals count all of them.
            </p>
          )}
        </RequestView>
      </section>
      {removed && !historyAction.error && (
        <div className="undo-toast" role="status">
          <span>Deleted the {label(String(removed.platform))} entry for {formatDate(String(removed.snapshot_date))}.</span>
          <button type="button" className="text-button" disabled={historyAction.busy} onClick={() => void undo()}>Undo</button>
        </div>
      )}
    </div>
  );
}
export default function Spend() {
  const { clientId } = useClientSelection();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'budgeting' ? 'budgeting' : params.get('view') === 'plan' ? 'plan' : 'entries';
  const budgeting = view !== 'entries';
  return (
    <>
      {/* The history below lists synced Google Ads and LSA rows too: the page holds all spend. */}
      <PageHeader
        title="Ad spend"
        description={budgeting ? undefined : "All ad spend, synced and entered. Platforms that don’t sync, like Meta, Yelp or Angi, are entered here."}
      />
      <nav className="portal-settings-nav" aria-label="Ad spend views">{[['entries','Spend entries'],['budgeting','Budget planner'],['plan','Investment plan']].map(([id,title]) => <button key={id} className={view === id ? 'active' : ''} aria-pressed={view === id} onClick={() => setParams(p => { id === 'entries' ? p.delete('view') : p.set('view', id); return p; })}>{title}</button>)}</nav>
      {clientId ? (
        view === 'plan' ? <InvestmentPlan key={clientId} clientId={clientId} /> : budgeting ? <MediaBudget key={clientId} clientId={clientId} /> : <SpendForm key={clientId} clientId={clientId} />
      ) : (
        <NoClient />
      )}
    </>
  );
}
